/**
 * Pitch Engine (Figma Tablet Edition)
 * Handles the perspective pitch interaction with a unified hitbox and B&W aesthetic.
 * Fully responsive: positions are recalculated from live DOM dimensions.
 */
window.PitchEngine = (function() {
    const DRESS_SIZE = 70;   // px — fixed jersey icon size
    const BANK_HEIGHT = 110; // px — matches #bank CSS height
    const GAP = 20;
    const LARGE_GAP = GAP * 2;
    const WHISTLE_RATIO = 0.7; // whistle is half the dress size

    let names = [];
    let dresses = [];
    const activeInPitch = new Set();
    
    let pitchContainer, bank, interactionArea, root;
    let onCompleteCallback = null;
    let resizeObserver = null;

    /**
     * Initialize the Pitch Engine
     */
    function init(playerNames, containerSelector, onComplete) {
        if (!playerNames || playerNames.length === 0) return false;

        names = playerNames;
        onCompleteCallback = onComplete;

        root = document.querySelector(containerSelector);
        if (!root) return false;

        root.innerHTML = `
            <div class="pitch-interaction-area">
                <div id="pitch-container">
                    <div id="pitch-surface">
                        <div class="pitch-line horz top"></div>
                        <div class="pitch-line horz mid"></div>
                        <div class="pitch-line vert center"></div>
                    </div>
                </div>
                <div id="bank"></div>
            </div>
        `;

        pitchContainer = root.querySelector('#pitch-container');
        bank = root.querySelector('#bank');
        interactionArea = root.querySelector('.pitch-interaction-area');

        dresses = [];
        activeInPitch.clear();

        // Create all dress elements (no positioning yet)
        names.forEach((name, i) => _createDressElement(i, name));

        // Create whistle element
        _createWhistleElement();

        // Wait for layout, then position everything
        setTimeout(() => {
            _recalculate();
        }, 50);

        // Watch for size changes and re-layout
        if (resizeObserver) resizeObserver.disconnect();
        resizeObserver = new ResizeObserver(() => _recalculate());
        resizeObserver.observe(root);

        return true;
    }

    /**
     * Recalculate all element positions from live DOM measurements.
     * Called on init and on every resize.
     */
    function _recalculate() {
        if (!pitchContainer || !bank || dresses.length === 0) return;

        const pOff = {
            left: pitchContainer.offsetLeft,
            top: pitchContainer.offsetTop,
            width: pitchContainer.offsetWidth,
            height: pitchContainer.offsetHeight
        };
        const bOff = {
            left: bank.offsetLeft,
            top: bank.offsetTop,
            width: bank.offsetWidth,
            height: bank.offsetHeight
        };

        // Compute dress/whistle size proportionally from pitch width (base: 70px at 379px pitch)
        const DRESS_LIVE = Math.max(28, Math.round(pOff.width * (70 / 379)));
        const WHISTLE_LIVE = Math.max(14, Math.round(DRESS_LIVE * WHISTLE_RATIO));

        const splitIndex = Math.ceil(names.length / 2);
        const group1Count = splitIndex;
        const group2Count = names.length - splitIndex;
        const bankLayoutWidth = (names.length * DRESS_LIVE) + (names.length - 2) * GAP + (2 * LARGE_GAP) + WHISTLE_LIVE;

        let currentX = bOff.left + (bOff.width - bankLayoutWidth) / 2;
        const homePositions = [];

        // Group 1
        for (let i = 0; i < group1Count; i++) {
            homePositions.push(currentX);
            currentX += DRESS_LIVE + GAP;
        }

        // Whistle gap + position
        currentX += (LARGE_GAP - GAP);
        const whistleX = currentX;
        const whistleY = bOff.top + (bOff.height - WHISTLE_LIVE) / 2;
        currentX += WHISTLE_LIVE + LARGE_GAP;

        // Group 2
        for (let i = 0; i < group2Count; i++) {
            homePositions.push(currentX);
            currentX += DRESS_LIVE + GAP;
        }

        // Update each dress: size always, position if not manually placed in pitch
        dresses.forEach((el, i) => {
            const index = parseInt(el.dataset.index);
            el.bankX = homePositions[i];
            el.bankY = bOff.top + (bOff.height - DRESS_LIVE) / 2;

            if (index === 0) {
                // Bertko always resets to pitch center on resize
                el.homeX = pOff.left + (pOff.width - DRESS_LIVE) / 2;
                el.homeY = pOff.top + pOff.height * 0.6;
                gsap.set(el, { x: el.homeX, y: el.homeY, width: DRESS_LIVE, height: DRESS_LIVE });
            } else if (!activeInPitch.has(index)) {
                // In bank — snap to new bank position and scale
                el.homeX = el.bankX;
                el.homeY = el.bankY;
                gsap.set(el, { x: el.homeX, y: el.homeY, width: DRESS_LIVE, height: DRESS_LIVE });
            } else {
                // In pitch — scale only, keep current position
                gsap.set(el, { width: DRESS_LIVE, height: DRESS_LIVE });
            }
        });

        // Update whistle position and scale
        const whistleEl = interactionArea.querySelector('.whistle-trigger');
        if (whistleEl) {
            gsap.set(whistleEl, { x: whistleX, y: whistleY, width: WHISTLE_LIVE, height: WHISTLE_LIVE });
        }
    }

    function _createDressElement(index, name) {
        const el = document.createElement('div');
        el.className = 'dress';
        el.dataset.index = index;
        el.dataset.name = name;
        el.innerHTML = `
            <img src="media/dres.svg" alt="Dres">
            <span class="dress-label">${name}</span>
        `;
        interactionArea.appendChild(el);
        dresses.push(el);

        if (index === 0) activeInPitch.add(0);
        
        _makeDraggable(el);
    }

    function _createWhistleElement() {
        const whistle = document.createElement('div');
        whistle.className = 'whistle-trigger';
        whistle.innerHTML = `<img src="media/pistala.webp" alt="Start the game!">`;
        interactionArea.appendChild(whistle);
        whistle.addEventListener('click', () => {
            whistle.disabled = true;
            whistle.style.pointerEvents = 'none';
            whistle.style.opacity = '0';
            const label = document.getElementById("interactive-label");
            label.textContent = typeof getLabel === 'function' ? getLabel("HRA") : "HRA";
            if (onCompleteCallback) onCompleteCallback();
            // const app = document.getElementById('pitch-app');
            //                 if (app) {
            //                     app.style.pointerEvents = 'none';
            //                     app.style.userSelect = 'none';
            //                     app.style.touchAction = 'none';
            //                 }
        });
    }

    function _makeDraggable(el) {
        Draggable.create(el, {
            onDrag: function() {
                gsap.set(this.target, { scale: 1.0 });
            },
            onRelease: function() {
                const index = parseInt(el.dataset.index);
                const status = _getPitchStatus(this.target);
                if (status.isInside) {
                    if (!activeInPitch.has(index) && activeInPitch.size >= 3) {
                        _returnToHome(this.target);
                    } else {
                        activeInPitch.add(index);
                        if (index === 0) {
                            // Bertko moved manually — no longer use default
                            el._usePitchDefault = false;
                            el.homeX = gsap.getProperty(el, "x");
                            el.homeY = gsap.getProperty(el, "y");
                        }
                    }
                } else {
                    _returnToHome(this.target);
                }
            }
        });
    }

    function _returnToHome(el) {
        const index = parseInt(el.dataset.index);
        activeInPitch.delete(index);
        gsap.to(el, {
            x: el.homeX,
            y: el.homeY,
            duration: 0.5,
            ease: "power2.inOut"
        });
    }

    function _getPitchStatus(el) {
        const pRect = pitchContainer.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const centerX = elRect.left + elRect.width / 2;
        const centerY = elRect.top + elRect.height / 2;
        const relX = centerX - pRect.left;
        const relY = centerY - pRect.top;
        const isInside = relX >= 0 && relX <= pRect.width && relY >= 0 && relY <= pRect.height;
        return { isInside, relX, relY };
    }

    function getResults() {
        return dresses.map(el => {
            const index = parseInt(el.dataset.index);
            const name = el.dataset.name;
            let percentage = 0;
            if (activeInPitch.has(index)) {
                const status = _getPitchStatus(el);
                const rawVertical = ((pitchContainer.offsetHeight - status.relY) / pitchContainer.offsetHeight) * 100;
                percentage = Math.max(0, Math.min(100, rawVertical));
            }
            return { name, percentage };
        });
    }

    return {
        init: init,
        getResults: getResults
    };
})();
